import React, { useEffect, useMemo, useState } from "react";
import {
  useFocusable,
  FocusContext,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import QRCode from "react-qr-code";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { logoutUser, setUser } from "../../redux/Slices/user.slice";
import { clearHomeShowcaseCache } from "../../utils/hooks/useHomeShowcaseData";
import { trackGoogleAnalyticsEvent } from "../../services/googleAnalytics";
import { getServerNow } from "../../services/publicAuthConfigService";
import {
  PROFILE_COLORS,
  PROFILE_FONT_SIZES,
  PROFILE_SPACING,
  PROFILE_RADIUS,
  PROFILE_SHADOWS,
  ICONS,
  FONT_FAMILY,
} from "./styles/profileStyles";
import ProfileSwitcherModal from "./ProfileSwitcherModal";
import { logout } from "../../services/authService";
import { clearAuthSession } from "../../services/authStorage";
import { resolveAuthMediaUrl } from "../../utils/api.utils";

// ============================================================================
// STYLED COMPONENTS (Table-based layout for legacy TV compatibility)
// ============================================================================

function profileElement(tag, className) {
  var ProfileElement = React.forwardRef(function ProfileElement(props, ref) {
    var mergedClassName = className + (props.className ? " " + props.className : "");
    return React.createElement(tag, Object.assign({}, props, { className: mergedClassName, ref: ref }));
  });

  ProfileElement.displayName = "ProfileElement";
  return ProfileElement;
}

var ContentWrapper = profileElement("div", "legacy-profile-content");
var ProfileCard = profileElement("div", "legacy-profile-card");
var SectionCard = profileElement("div", "legacy-profile-section");

// Table-based layout for user info
var UserTable = profileElement("table", "legacy-profile-user-table");
var UserTableCell = profileElement("td", "legacy-profile-user-cell");
var Avatar = function Avatar(props) {
  var style = Object.assign(
    {
      background: props.$hasImage ? "url(" + props.$imageUrl + ")" : PROFILE_COLORS.accentLight,
      backgroundSize: "cover",
      backgroundPosition: "center",
    },
    props.style || {}
  );
  return <div className="legacy-profile-avatar" style={style}>{props.children}</div>;
};
var AvatarInner = profileElement("div", "legacy-profile-avatar-inner");
var UserInfoWrapper = profileElement("div", "legacy-profile-user-info");
var UserName = profileElement("div", "legacy-profile-user-name");
var UserPhone = profileElement("div", "legacy-profile-user-phone");
var Divider = profileElement("div", "legacy-profile-divider");
var SubscriptionInfo = profileElement("div", "legacy-profile-subscription-info");
var SubscriptionText = function SubscriptionText(props) {
  return (
    <div className={"legacy-profile-subscription-text" + (props.$hasSubscription ? " has-subscription" : "")}>
      {props.children}
    </div>
  );
};
var SubscriptionDays = function SubscriptionDays(props) {
  return <span className={"legacy-profile-subscription-days" + (props.$isLow ? " is-low" : "")}>{props.children}</span>;
};
var QRSection = profileElement("div", "legacy-profile-qr-section");
var QRLabel = profileElement("div", "legacy-profile-qr-label");
var QRWrapper = profileElement("div", "legacy-profile-qr-wrapper");

// Menu Grid - Table based
var MenuTable = profileElement("table", "legacy-profile-menu-table");
var MenuRow = profileElement("tr", "legacy-profile-menu-row");
var MenuCell = profileElement("td", "legacy-profile-menu-cell");

// Focusable Button Component
var MenuButtonStyled = React.forwardRef(function MenuButtonStyled(props, ref) {
  var buttonProps = Object.assign({}, props);
  delete buttonProps.$focused;
  delete buttonProps.$danger;
  buttonProps.className =
    "legacy-profile-menu-button" +
    (props.$focused ? " is-focused" : "") +
    (props.$danger ? " is-danger" : "") +
    (props.className ? " " + props.className : "");
  buttonProps.ref = ref;
  return <button {...buttonProps} />;
});
MenuButtonStyled.displayName = "MenuButtonStyled";

var ButtonIcon = profileElement("span", "legacy-profile-button-icon");

var SubscriptionButton = React.forwardRef(function SubscriptionButton(props, ref) {
  var buttonProps = Object.assign({}, props);
  delete buttonProps.$focused;
  buttonProps.className =
    "legacy-profile-subscription-button" +
    (props.$focused ? " is-focused" : "") +
    (props.className ? " " + props.className : "");
  buttonProps.ref = ref;
  return <button {...buttonProps} />;
});
SubscriptionButton.displayName = "SubscriptionButton";

// ============================================================================
// FOCUSABLE MENU BUTTON COMPONENT
// ============================================================================

function FocusableMenuButton({ label, icon, onPress, danger, focusKey }) {
  var { ref, focused } = useFocusable({
    focusKey: focusKey,
    onEnterPress: onPress,
  });

  return (
    <MenuButtonStyled
      ref={ref}
      onClick={onPress}
      $focused={focused}
      $danger={danger}
    >
      {icon && <ButtonIcon>{icon}</ButtonIcon>}
      {label}
    </MenuButtonStyled>
  );
}

// ============================================================================
// FOCUSABLE SUBSCRIPTION BUTTON
// ============================================================================

function FocusableSubscriptionButton({ onPress }) {
  var { ref, focused } = useFocusable({
    focusKey: "buy-subscription",
    onEnterPress: onPress,
  });

  return (
    <SubscriptionButton ref={ref} onClick={onPress} $focused={focused}>
      <ButtonIcon>{ICONS.cart}</ButtonIcon>
      خرید اشتراک
    </SubscriptionButton>
  );
}

// ============================================================================
// MAIN PROFILE CONTENT COMPONENT
// ============================================================================

var ProfilePageContent = function (props) {
  var data = props.data;
  var user = useSelector(function (state) { return state.User; });
  var dispatch = useDispatch();
  var navigate = useNavigate();

  var { ref, focusKey } = useFocusable({
    focusKey: "PROFILE_CONTENT",
    isFocusBoundary: false,
    preferredChildFocusKey: "menu-bookmarks",
  });

  // Get subscription days from API response
  var subDays = useMemo(function () {
    try {
      var activeSubscription = data.activeSubscription;
      if (!activeSubscription) {
        return "";
      }
      // Use remainingAccessDays from the new API
      if (typeof activeSubscription.remainingAccessDays === 'number') {
        return activeSubscription.remainingAccessDays;
      }
      // Fallback to calculating from expireDate if available
      if (activeSubscription.subscriptionInfo?.expireDate) {
        var expireDate = new Date(activeSubscription.subscriptionInfo.expireDate);
        var timeDiff = expireDate.getTime() - getServerNow();
        return Math.round(timeDiff / (1000 * 60 * 60 * 24));
      }
      return "";
    } catch (error) {
      return "";
    }
  }, [data]);

  // Set user data on mount
  useEffect(function () {
    if (data) {
      dispatch(setUser(data));
    }
  }, [data, dispatch]);

  // Set initial focus
  useEffect(function () {
    var timer = setTimeout(function () {
      setFocus("menu-bookmarks");
    }, 150);
    return function () { clearTimeout(timer); };
  }, []);

  // Navigation handlers
  function handleFavourites() {
    navigate("/profile/favourites");
  }

  function handleWatchHistory() {
    navigate("/profile/watch-history");
  }

  function handleSubscriptions() {
    navigate("/profile/subscriptions");
  }

  function handleBuySubscription() {
    trackGoogleAnalyticsEvent("purchase_started", {
      purchase_type: "subscription",
      source: "profile_buy_subscription",
    });
    navigate("/subscription");
  }

  async function handleLogout() {
    dispatch(logoutUser());
    try {
      await logout();
    } catch (error) {
      // Local cleanup is authoritative when the server is unreachable.
      clearAuthSession();
    }
    clearHomeShowcaseCache();
    toast.success("با موفقیت از حساب خود خارج شدید");
    navigate("/");
  }

  // Profile switcher modal state
  var [showProfileSwitcher, setShowProfileSwitcher] = useState(false);
  var profiles = data?.profiles || [];
  var currentProfileId = data?.currentProfileId || data?._id;

  function handleSwitchProfile() {
    if (profiles.length === 0) {
      toast.error("پروفایل دیگری یافت نشد");
      return;
    }
    setShowProfileSwitcher(true);
  }

  function handleCloseSwitcher() {
    setShowProfileSwitcher(false);
    setTimeout(function () {
      setFocus("menu-switch-profile");
    }, 150);
  }

  // Get user display info
  var userName = user?.full_name || "کاربر سایت";
  var userInitial = userName.charAt(0) || "ک";
  var mobileNumber = user?.mobileNumber ? "0" + user.mobileNumber : "";
  var profileImage = data?.profileImage || user?.profileImage;
  var profileImageUrl = resolveAuthMediaUrl(profileImage);
  var hasProfileImage = profileImage && profileImage.length > 0;
  var hasSubscription = subDays && subDays > 0;

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentWrapper ref={ref}>
        <ProfileCard>
          {/* User Info Section */}
          <SectionCard>
            <UserTable>
              <tbody>
                <tr>
                  {/* Avatar */}
                  <UserTableCell style={{ width: "90px" }}>
                    <Avatar $hasImage={hasProfileImage} $imageUrl={profileImageUrl}>
                      {!hasProfileImage && <AvatarInner>{userInitial}</AvatarInner>}
                    </Avatar>
                  </UserTableCell>

                  {/* User Name & Phone */}
                  <UserTableCell style={{ width: "200px" }}>
                    <UserInfoWrapper>
                      <UserName>{userName}</UserName>
                      <UserPhone>{mobileNumber}</UserPhone>
                    </UserInfoWrapper>
                  </UserTableCell>

                  {/* Divider */}
                  <UserTableCell style={{ width: "50px" }}>
                    <Divider />
                  </UserTableCell>

                  {/* Subscription Info */}
                  <UserTableCell>
                    <SubscriptionInfo>
                      {hasSubscription ? (
                        <SubscriptionText $hasSubscription={true}>
                          <SubscriptionDays $isLow={subDays < 10}>{subDays}</SubscriptionDays> روز از اشتراک باقی مانده
                        </SubscriptionText>
                      ) : (
                        <SubscriptionText $hasSubscription={false}>
                          {ICONS.warning} اشتراک فعال ندارید
                        </SubscriptionText>
                      )}
                    </SubscriptionInfo>
                  </UserTableCell>

                  {/* QR Code (only when has subscription) */}
                  {hasSubscription && (
                    <UserTableCell style={{ width: "280px" }}>
                      <QRSection>
                        <QRLabel>خرید اشتراک</QRLabel>
                        <QRWrapper>
                          <QRCode
                            size={240}
                            style={{ width: "100%", height: "auto" }}
                            value="https://mnbx.ir/subscription"
                            bgColor={PROFILE_COLORS.textSecondary}
                            fgColor={PROFILE_COLORS.background}
                            viewBox="0 0 256 256"
                          />
                        </QRWrapper>
                      </QRSection>
                    </UserTableCell>
                  )}
                </tr>
              </tbody>
            </UserTable>
          </SectionCard>

          {/* Menu Grid */}
          <MenuTable>
            <tbody>
              <MenuRow>
                <MenuCell>
                  <FocusableMenuButton
                    focusKey="menu-bookmarks"
                    label="نشان شده ها"
                    onPress={handleFavourites}
                  />
                </MenuCell>
                <MenuCell>
                  <FocusableMenuButton
                    focusKey="menu-history"
                    label="تاریخچه تماشا"
                    onPress={handleWatchHistory}
                  />
                </MenuCell>
                <MenuCell>
                  <FocusableMenuButton
                    focusKey="menu-switch-profile"
                    label="تغییر پروفایل"
                    onPress={handleSwitchProfile}
                  />
                </MenuCell>
                <MenuCell>
                  <FocusableMenuButton
                    focusKey="menu-logout"
                    label="خروج از حساب"
                    onPress={handleLogout}
                    danger={true}
                  />
                </MenuCell>
              </MenuRow>
            </tbody>
          </MenuTable>

          {/* Buy Subscription Button (when no subscription) */}
          {!hasSubscription && (
            <FocusableSubscriptionButton onPress={handleBuySubscription} />
          )}
        </ProfileCard>
      </ContentWrapper>

      {/* Profile Switcher Modal */}
      <ProfileSwitcherModal
        open={showProfileSwitcher}
        profiles={profiles}
        currentProfileId={currentProfileId}
        onClose={handleCloseSwitcher}
      />
    </FocusContext.Provider>
  );
};

export default ProfilePageContent;
